import { PostSortRowsParams } from "ag-grid-community";
import { ClusterProjectRow } from "in_queue/types/clusterProjectRowType";
import {
    isPriorQueueProject,
    Project,
    ProjectSize,
    ProjectType
} from "in_queue/types/projectType";
import { MisoPercentageReduction } from "in_queue/types/scenarioType";
import { isProjectExcluded } from "in_queue/types/scenarioUtils";
import { keyBy } from "lodash";
import { ProjectSelection } from "./defaultProjectSelection";

export interface ProjectPickerRow {
    readonly project: Project;
    readonly isCurrentProject: boolean;
    readonly isTracked: boolean;
    readonly isPriorQueue: boolean;
    readonly isExcluded: boolean;
    readonly reduction: MisoPercentageReduction | undefined;
    readonly stats: ClusterProjectRow | undefined;
}

const isRowExcluded = (
    project: Project,
    selection: ProjectSelection
): boolean =>
    project.type === ProjectType.SPECULATIVE_PROJECT
        ? !selection.includedSpeculativeProjectIds.includes(project.projectId)
        : isProjectExcluded(
              selection.projectSizeAssumptions[project.projectId]
          );

export const buildProjectPickerRows = ({
    allProjects,
    clusterRows,
    selection,
    currentProjectId,
    portfolioProjectIds,
    currentCluster
}: {
    readonly allProjects: ReadonlyArray<Project>;
    readonly clusterRows: ReadonlyArray<ClusterProjectRow>;
    readonly selection: ProjectSelection;
    readonly currentProjectId: string;
    readonly portfolioProjectIds: ReadonlySet<string>;
    readonly currentCluster: { studyCycle: string };
}): ReadonlyArray<ProjectPickerRow> => {
    const statsByProjectId = keyBy(clusterRows, "projectId");

    return allProjects.map((project) => ({
        project,
        isCurrentProject: project.projectId === currentProjectId,
        isTracked: portfolioProjectIds.has(project.projectId),
        isPriorQueue: isPriorQueueProject(project.cluster, currentCluster),
        isExcluded: isRowExcluded(project, selection),
        reduction: selection.projectSizeAssumptions[project.projectId],
        stats: statsByProjectId[project.projectId]
    }));
};

// Rounding at 0% would turn 399.035 into 399.04 and render it as a reduction.
// Hundredths match the decimals the MW cells accept.
export const applyReduction = (sizeMw: number, percentage: number): number =>
    percentage === 0
        ? sizeMw
        : Math.round(((sizeMw * (100 - percentage)) / 100) * 100) / 100;

/** The reduction percentage that leaves `targetMw` of `sizeMw`, precise enough that applyReduction returns `targetMw`. */
export const reductionForSize = (sizeMw: number, targetMw: number): number =>
    Math.round((1 - targetMw / sizeMw) * 1e6) / 1e4;

export const reducedSize = (row: ProjectPickerRow): ProjectSize =>
    row.isExcluded
        ? { erisMw: 0, nrisMw: 0 }
        : row.reduction === undefined
        ? row.project.size
        : {
              erisMw: applyReduction(
                  row.project.size.erisMw,
                  row.reduction.eris
              ),
              nrisMw: applyReduction(
                  row.project.size.nrisMw,
                  row.reduction.nris
              )
          };

interface ReductionChange {
    readonly eris?: number;
    readonly nris?: number;
}

/**
 * Whether a current-cycle project would end up with reduced ERIS MW below
 * reduced NRIS MW once `change` applies on top of its current reduction.
 * MISO does not allow NRIS to exceed ERIS. Prior-queue and NRIS-only projects
 * never qualify: PQ ERIS is withdraw-only, and NRIS-only projects start with
 * no ERIS.
 */
export const isErisBelowNris = (
    row: ProjectPickerRow,
    change: ReductionChange,
    capacityOnlyProjectIds: ReadonlySet<string>
): boolean => {
    const { size } = row.project;
    if (
        capacityOnlyProjectIds.has(row.project.projectId) ||
        size.erisMw < size.nrisMw
    ) {
        return false;
    }
    return (
        applyReduction(size.erisMw, change.eris ?? row.reduction?.eris ?? 0) <
        applyReduction(size.nrisMw, change.nris ?? row.reduction?.nris ?? 0)
    );
};

export const countErisBelowNris = ({
    rows,
    changes,
    capacityOnlyProjectIds
}: {
    readonly rows: ReadonlyArray<ProjectPickerRow>;
    readonly changes: ReadonlyArray<
        ReductionChange & { readonly projectId: string }
    >;
    readonly capacityOnlyProjectIds: ReadonlySet<string>;
}): number => {
    const rowsById = keyBy(rows, (row) => row.project.projectId);
    return changes.filter(({ projectId, ...change }) => {
        const row = rowsById[projectId];
        return (
            row !== undefined &&
            isErisBelowNris(row, change, capacityOnlyProjectIds)
        );
    }).length;
};

export const NRIS_EXCEEDS_ERIS_TARIFF =
    "MISO's tariff states: \u201cThe total amount of NRIS requested shall not exceed the amount of ERIS requested.\u201d";

const orderRank = (row: ProjectPickerRow): number => {
    if (row.isCurrentProject) return 0;
    if (row.isTracked) return row.isPriorQueue ? 2 : 1;
    return row.isPriorQueue ? 4 : 3;
};

export const projectPickerPostSort = (
    params: PostSortRowsParams<ProjectPickerRow>
): void => {
    if (params.api.getColumnState().some((column) => column.sort)) {
        return;
    }
    params.nodes.sort(
        (a, b) =>
            (a.data ? orderRank(a.data) : 0) - (b.data ? orderRank(b.data) : 0)
    );
};
